import { ProjectCardMention } from "@/components/cards";
import { Address } from "@msuite/ui/address";
import { Divider } from "@msuite/ui/divider";
import { Link } from "@msuite/ui/link";
import { localized } from "@msuite/ui/localization";
import { OverflowList } from "@msuite/ui/overflow-list";
import { Popover } from "@msuite/ui/popover";
import { createColumnHelper, createMeta, createRowActions } from "@msuite/ui/table";
import { ProjectStateTag, Tag, WorkspaceTag } from "@msuite/ui/tag";
import { getProjectStateString } from "@msuite/utils/projects";
import { getAddressString, guardString } from "@msuite/utils/strings";
import type { ProjectCardWithInvoices } from "@msuite/utils/types";
import { getFullName } from "@msuite/utils/users";
import { Fragment } from "react";
import { LastInvoice } from "./last-invoice";
import { LastInvoiceCell } from "./last-invoice-cell";
import { RowActions } from "./row-actions";
import { getInvoiceData, invoiceDataSortingFn } from "./shared";

const columnHelper = createColumnHelper<ProjectCardWithInvoices>();

export const columns = [
	columnHelper.accessor("_workspace.name", {
		header: localized("organization.workspace"),
		id: "workspaceName",
		cell: (cell) => <WorkspaceTag workspace={cell.row.original._workspace} />,
	}),
	columnHelper.accessor("number", {
		header: localized("projects.fields.project_number"),
		id: "number",
		cell: (cell) => guardString(cell.getValue()),
		meta: createMeta({
			isTabular: true,
		}),
	}),
	columnHelper.accessor("name", {
		header: localized("projects.fields.project_name"),
		id: "name",
		cell: (cell) => guardString(cell.getValue()),
	}),
	columnHelper.accessor((row) => getAddressString(row), {
		header: localized("address.address"),
		id: "address",
		cell: (cell) => <Address item={cell.row.original} />,
	}),
	columnHelper.accessor((row) => guardString(row._client?.name), {
		header: localized("projects.fields.client"),
		id: "client",
		cell: (cell) =>
			cell.row.original._client?.id ? (
				<Link href={`/contacts/${cell.row.original._client?.id}`}>
					{guardString(cell.getValue())}
				</Link>
			) : (
				guardString(null)
			),
	}),
	columnHelper.accessor((row) => row._relatedProject?.name || "", {
		header: localized("projects.fields.related_project"),
		id: "relatedProject",
		cell: (cell) =>
			cell.row.original._relatedProject?.id ? (
				<ProjectCardMention projectId={cell.row.original._relatedProject?.id} />
			) : (
				guardString(null)
			),
	}),
	columnHelper.accessor((row) => localized(getProjectStateString(row.state)), {
		header: localized("general_fields.state"),
		id: "state",
		cell: (cell) => <ProjectStateTag state={cell.row.original.state} />,
	}),
	columnHelper.accessor(
		(row) => row._members.map((member) => getFullName(member._user)).join(", "),
		{
			header: localized("projects.fields.manager"),
			id: "members",
			cell: (cell) => (
				<div className="absolute inset-y-0 inset-x-2 h-stack items-center">
					<OverflowList
						items={cell.row.original._members}
						renderItem={(item) => (
							<Tag
								size="xs"
								withStatusIndicator={false}
								color="gray"
							>
								{item._user.name}
							</Tag>
						)}
						renderOverflowItem={({ overflowCount, hiddenItems }) => (
							<Popover
								trigger="hover"
								render={() => (
									<div className="v-stack">
										{hiddenItems.map((item) => (
											<Fragment key={item.id}>
												<p>{getFullName(item._user)}</p>
												<Divider variant="card" />
											</Fragment>
										))}
									</div>
								)}
							>
								<Tag
									size="xs"
									color="gray"
									withStatusIndicator={false}
								>
									+ {overflowCount}
								</Tag>
							</Popover>
						)}
					/>
				</div>
			),
		},
	),
	columnHelper.accessor((row) => getInvoiceData(row), {
		header: () => <LastInvoice.Header />,
		id: "invoiceData",
		cell: (cell) => <LastInvoiceCell cell={cell} />,
		size: 500,
		sortFn: invoiceDataSortingFn,
	}),
	createRowActions(RowActions),
];
